import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { SEO } from "../components/SEO";
import type { ReactNode } from "react";
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  ClipboardList,
  LineChart,
  Link2,
  Quote,
  Shield,
  Sparkles,
  Wrench,
  Zap,
  Building2,
  Award,
} from "lucide-react";

const BOOKING_URL = "https://consult.optiwizer.com/#/book_consultation";

const AI_CONSULTATION_HERO_IMAGE = `/assets/${encodeURIComponent("AI Consultation Hero Image.png")}`;

function BookConsultationLink({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className={className}>
      {children}
    </a>
  );
}

function MidPageCtaBand() {
  return (
    <section
      className="py-10 bg-gradient-to-r from-blue-600 to-purple-600 text-white animate-in fade-in duration-700"
      aria-label="Book a consultation"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <BookConsultationLink>
            <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 w-full sm:w-auto text-lg px-10 py-4">
              Book AI Consultation
            </Button>
          </BookConsultationLink>
          <Button
            size="lg"
            variant="outline"
            className="border-white text-white hover:bg-white/10 w-full sm:w-auto text-lg px-10 py-4"
            asChild
          >
            <a href="#use-cases">Explore Use Cases</a>
          </Button>
        </div>
      </div>
    </section>
  );
}

export default function AIConsultationPage() {
  const problemItems = [
    {
      icon: Wrench,
      text: "Starting with tools instead of business problems",
    },
    {
      icon: ClipboardList,
      text: "Implementing AI without structured processes",
    },
    {
      icon: LineChart,
      text: "Investing in technology but underutilizing it",
    },
    {
      icon: Link2,
      text: "Lack of integration across systems",
    },
  ];

  const approachSteps = [
    {
      step: 1,
      title: "Business Process Mapping",
      description: "Understand how your business actually operates",
    },
    {
      step: 2,
      title: "Identify AI Opportunities",
      description: "Pinpoint high-impact areas for automation and intelligence",
    },
    {
      step: 3,
      title: "Architecture & Tool Design",
      description: "Define the right mix of platforms, integrations, and AI layers",
    },
    {
      step: 4,
      title: "Implementation & Automation",
      description: "Deploy workflows, bots, and AI-assisted processes",
    },
    {
      step: 5,
      title: "Optimization",
      description: "Continuously improve based on real usage and outcomes",
    },
  ];

  const useCaseCategories = [
    {
      title: "Sales & CRM",
      items: [
        "Lead scoring and prioritization",
        "Automated follow-ups and responses",
        "Sales forecasting",
      ],
    },
    {
      title: "Operations",
      items: [
        "Workflow automation",
        "Order processing & tracking",
        "Inventory planning",
      ],
    },
    {
      title: "Customer Support",
      items: [
        "AI chatbots (including WhatsApp automation)",
        "Ticket classification & routing",
        "Response suggestions",
      ],
    },
    {
      title: "Finance",
      items: ["Invoice processing", "Expense categorization", "Data validation"],
    },
    {
      title: "HR & Internal Operations",
      items: [
        "Resume screening",
        "Employee query automation",
        "Performance insights",
      ],
    },
  ];

  const techStack = [
    "Zoho ecosystem (CRM, Books, Inventory, HR, Creator)",
    "AI/LLM integrations",
    "WhatsApp automation",
    "Custom business apps",
    "API-based integrations",
  ];

  const engagementModels = [
    {
      title: "AI Discovery Workshop",
      bullets: [
        "Understand your business processes",
        "Identify AI opportunities",
        "Deliver a practical roadmap",
      ],
    },
    {
      title: "AI Implementation Project",
      bullets: [
        "End-to-end execution",
        "Process design + automation + integration",
      ],
    },
    {
      title: "Ongoing AI Advisory",
      bullets: ["Continuous improvement", "Optimization and scaling"],
    },
  ];

  const impactStories = [
    "Custom ERP systems improving procurement and execution efficiency",
    "CRM automation reducing manual sales effort",
    "Workflow automation saving operational costs",
  ];

  const whyUs = [
    {
      icon: Award,
      text: "20+ years of business and technology experience",
    },
    {
      icon: Zap,
      text: "Strong expertise in process automation",
    },
    {
      icon: Building2,
      text: "Deep understanding of CRM, ERP, and operations",
    },
    {
      icon: CheckCircle2,
      text: "Proven track record of building real business systems",
    },
  ];

  const webPageJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "AI Consultation for Business Automation | Optiwizer Technologies",
    description:
      "Practical AI consulting for founders and business leaders to automate sales, operations, and workflows.",
    url: "https://optiwizer.com/ai-consultation",
  };

  return (
    <>
      <SEO
        title="AI Consultation for Business Automation | Optiwizer Technologies"
        description="Practical AI consulting for founders and business leaders to automate sales, operations, and workflows."
        canonical="https://optiwizer.com/ai-consultation"
        ogImage="/assets/logos/optiwizer-logo.png"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageJsonLd) }}
      />

      {/* Sticky CTA */}
      <div className="fixed bottom-4 right-4 left-4 sm:left-auto z-40 flex justify-center sm:justify-end pointer-events-none">
        <BookConsultationLink className="pointer-events-auto shadow-lg rounded-md">
          <Button
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white w-full sm:w-auto px-6 py-6 text-lg font-semibold shadow-lg"
          >
            <Calendar className="mr-2 h-5 w-5 shrink-0" aria-hidden />
            Book Consultation
          </Button>
        </BookConsultationLink>
      </div>

      {/* SECTION 1: HERO — full-width background image (theme matches Home / Services) */}
      <section
        className="relative isolate w-full min-h-[min(90svh,880px)] flex items-center pt-24 lg:pt-28 pb-16 lg:pb-20 animate-in fade-in duration-700 overflow-hidden"
        aria-labelledby="ai-consult-hero-heading"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url("${AI_CONSULTATION_HERO_IMAGE}")` }}
          aria-hidden
        />
        {/* Light veil — softens the photo for readability */}
        <div className="absolute inset-0 bg-white/60" aria-hidden />
        {/* Tinted wash: site palette (blue-50 / indigo-50 / white) */}
        <div
          className="absolute inset-0 bg-gradient-to-br from-blue-50/80 via-white/55 to-indigo-50/75"
          aria-hidden
        />
        <div className="absolute inset-0 opacity-[0.35] pointer-events-none" aria-hidden>
          <div className="absolute top-16 left-8 w-28 h-28 bg-blue-600 rounded-full blur-3xl" />
          <div className="absolute bottom-24 right-12 w-40 h-40 bg-purple-600 rounded-full blur-3xl" />
          <div className="absolute top-1/3 right-1/4 w-24 h-24 bg-indigo-600 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 text-center">
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            <Badge className="bg-blue-100 text-blue-800 border-0 hover:bg-blue-100">
              <Shield className="h-3.5 w-3.5 mr-1.5" aria-hidden />
              Trusted Process
            </Badge>
            <Badge className="bg-blue-100 text-blue-800 border-0 hover:bg-blue-100">
              20+ Years Experience
            </Badge>
          </div>
          <h1
            id="ai-consult-hero-heading"
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 tracking-tight mb-6 leading-tight"
          >
            Make AI Work for Your Business{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              — Not the Other Way Around
            </span>
          </h1>
          <p className="text-xl lg:text-2xl text-gray-600 mb-4 max-w-4xl mx-auto leading-relaxed">
            Practical AI consulting to identify, design, and implement automation across your sales,
            operations, finance, and customer workflows.
          </p>
          <p className="text-lg lg:text-xl text-gray-700 font-medium mb-10 max-w-4xl mx-auto">
            No jargon. No experimentation. Only business outcomes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch sm:items-center">
            <div className="flex flex-col items-center gap-2">
              <span className="text-lg" aria-hidden>
                👉
              </span>
              <BookConsultationLink>
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white text-lg px-10 py-4 w-full sm:w-auto shadow-xl"
                >
                  Book AI Consultation
                </Button>
              </BookConsultationLink>
            </div>
            <div className="flex flex-col items-center gap-2">
              <span className="text-lg" aria-hidden>
                👉
              </span>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-gray-300 text-gray-900 hover:bg-gray-50 w-full sm:w-auto text-lg px-10 py-4"
                asChild
              >
                <a href="#use-cases">
                  Explore Use Cases
                  <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: TARGET AUDIENCE */}
      <section
        className="py-16 lg:py-20 bg-white animate-in fade-in slide-in-from-bottom-2 duration-700"
        aria-labelledby="target-audience-heading"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="target-audience-heading" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Is This Relevant for You?
          </h2>
          <p className="text-xl text-gray-600 mb-6">This service is designed for:</p>
          <ul className="space-y-4 text-gray-700 text-lg lg:text-xl">
            <li className="flex gap-3">
              <CheckCircle2 className="h-6 w-6 text-blue-600 shrink-0 mt-0.5" aria-hidden />
              <span>Founders exploring AI but unsure where to start</span>
            </li>
            <li className="flex gap-3">
              <CheckCircle2 className="h-6 w-6 text-blue-600 shrink-0 mt-0.5" aria-hidden />
              <span>Businesses already using CRM/ERP but not leveraging AI fully</span>
            </li>
            <li className="flex gap-3">
              <CheckCircle2 className="h-6 w-6 text-blue-600 shrink-0 mt-0.5" aria-hidden />
              <span>Teams struggling with manual processes, follow-ups, or reporting</span>
            </li>
            <li className="flex gap-3">
              <CheckCircle2 className="h-6 w-6 text-blue-600 shrink-0 mt-0.5" aria-hidden />
              <span>Leaders looking to reduce operational cost and improve efficiency</span>
            </li>
          </ul>
          <p className="mt-10 text-gray-700 text-lg lg:text-xl leading-relaxed">
            If you&apos;re thinking &quot;AI can help, but how exactly?&quot; — you&apos;re in the right place.
          </p>
        </div>
      </section>

      {/* SECTION 3: PROBLEM */}
      <section
        className="py-16 lg:py-20 bg-gray-50 animate-in fade-in duration-700"
        aria-labelledby="problem-heading"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="problem-heading" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Why Most AI Initiatives Fail
          </h2>
          <p className="text-xl text-gray-600 mb-10">Most businesses approach AI the wrong way:</p>
          <ul className="space-y-6">
            {problemItems.map((item) => (
              <li key={item.text} className="flex gap-4 items-start">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                  <item.icon className="h-6 w-6" aria-hidden />
                </span>
                <span className="text-gray-800 text-lg lg:text-xl pt-2">{item.text}</span>
              </li>
            ))}
          </ul>
          <p className="mt-12 text-gray-900 font-semibold text-lg lg:text-xl border-l-4 border-blue-600 pl-4">
            AI doesn&apos;t fix broken processes — it amplifies them.
          </p>
        </div>
      </section>

      <MidPageCtaBand />

      {/* SECTION 4: APPROACH */}
      <section
        className="py-16 lg:py-20 bg-white animate-in fade-in duration-700"
        aria-labelledby="approach-heading"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-12">
            <h2 id="approach-heading" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Approach: Process First, AI Second
            </h2>
            <p className="text-xl text-gray-600">
              We focus on applying AI where it creates real business impact.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {approachSteps.map((s, i) => (
              <Card
                key={s.step}
                className="hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 border-gray-200 shadow-sm animate-in fade-in slide-in-from-bottom-2"
                style={{ animationDelay: `${i * 75}ms` }}
              >
                <CardHeader className="pb-2">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 text-white font-bold text-lg shadow-lg mb-2">
                    {s.step}
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900 leading-tight">
                    {s.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-gray-600 leading-relaxed">{s.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5: USE CASES */}
      <section
        id="use-cases"
        className="py-16 lg:py-20 bg-gray-50 scroll-mt-24 animate-in fade-in duration-700"
        aria-labelledby="use-cases-heading"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="use-cases-heading" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center">
            Where AI Can Transform Your Business
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {useCaseCategories.map((cat, i) => (
              <Card
                key={cat.title}
                className="hover:shadow-lg transition-all duration-300 border-gray-200 shadow-sm animate-in fade-in"
                style={{ animationDelay: `${i * 50}ms` }}
              >
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-blue-600 shrink-0" aria-hidden />
                    {cat.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-gray-600 text-sm">
                    {cat.items.map((line) => (
                      <li key={line} className="flex gap-2">
                        <span className="text-blue-600 shrink-0">•</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6: TECH STACK */}
      <section
        className="py-16 lg:py-20 bg-white animate-in fade-in duration-700"
        aria-labelledby="tech-stack-heading"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 id="tech-stack-heading" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Technology Stack We Work With
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            We combine proven business platforms with AI capabilities:
          </p>
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-6 py-4">
              <img
                src="/assets/logos/zoho-logo.png"
                alt="Zoho"
                className="h-8 w-auto object-contain"
              />
              <span className="text-base text-gray-600 text-left">Zoho ecosystem partner expertise</span>
            </div>
          </div>
          <div className="flex flex-wrap justify-center gap-3 mb-10">
            {techStack.map((label) => (
              <Badge
                key={label}
                variant="secondary"
                className="px-4 py-2 text-sm font-normal text-gray-800 bg-gray-100 hover:bg-gray-200 border border-gray-200"
              >
                {label}
              </Badge>
            ))}
          </div>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            We don&apos;t push tools — we design the right solution for your business.
          </p>
        </div>
      </section>

      <MidPageCtaBand />

      {/* SECTION 7: ENGAGEMENT */}
      <section
        className="py-16 lg:py-20 bg-gray-50 animate-in fade-in duration-700"
        aria-labelledby="engagement-heading"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="engagement-heading" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-12 text-center">
            How We Work Together
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {engagementModels.map((model, i) => (
              <Card
                key={model.title}
                className="relative overflow-hidden border-2 border-gray-200 bg-white hover:border-blue-400 hover:shadow-xl transition-all duration-300 animate-in fade-in"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  {i + 1}
                </div>
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-gray-900 pr-8">{model.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {model.bullets.map((b) => (
                      <li key={b} className="flex gap-2 text-sm text-gray-600">
                        <CheckCircle2 className="h-5 w-5 text-blue-600 shrink-0" aria-hidden />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8: SOCIAL PROOF */}
      <section
        className="py-16 lg:py-20 bg-white animate-in fade-in duration-700"
        aria-labelledby="impact-heading"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="impact-heading" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 text-center">
            Real Business Impact
          </h2>
          <p className="text-center text-sm text-gray-500 mb-10">
            Representative outcomes from client engagements (illustrative examples)
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {impactStories.map((story) => (
              <Card
                key={story}
                className="border-gray-200 bg-gray-50/80 hover:shadow-md transition-all duration-300"
              >
                <CardContent className="pt-6">
                  <Quote className="h-8 w-8 text-blue-300 mb-3" aria-hidden />
                  <p className="text-sm text-gray-700 leading-relaxed">{story}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="mt-10 text-center text-gray-600 text-xl">
            (Your business can achieve similar or higher impact with the right AI strategy.)
          </p>
        </div>
      </section>

      {/* SECTION 9: WHY US */}
      <section
        className="py-16 lg:py-20 bg-gray-50 animate-in fade-in duration-700"
        aria-labelledby="why-us-heading"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="why-us-heading" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-10 text-center">
            Why Optiwizer Technologies
          </h2>
          <ul className="space-y-6">
            {whyUs.map((row) => (
              <li key={row.text} className="flex gap-4 items-start">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                  <row.icon className="h-5 w-5" aria-hidden />
                </span>
                <span className="text-gray-800 text-lg lg:text-xl pt-2">{row.text}</span>
              </li>
            ))}
          </ul>
          <p className="mt-12 text-center text-gray-700 text-xl font-medium">
            We bridge the gap between business understanding and technology execution.
          </p>
        </div>
      </section>

      {/* SECTION 10: FINAL CTA */}
      <section
        className="py-20 lg:py-24 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 text-white animate-in fade-in duration-700 relative overflow-hidden"
        aria-labelledby="final-cta-heading"
      >
        <div className="absolute inset-0 opacity-20 pointer-events-none" aria-hidden>
          <div className="absolute top-0 right-0 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-300 rounded-full blur-3xl" />
        </div>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 id="final-cta-heading" className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6">
            Start with Clarity, Not Confusion
          </h2>
          <p className="text-lg lg:text-xl text-blue-50 mb-10 leading-relaxed max-w-2xl mx-auto">
            Before investing in AI tools, understand where it truly fits in your business.
          </p>
          <div className="flex flex-col items-center gap-3">
            <span className="text-2xl" aria-hidden>
              👉
            </span>
            <BookConsultationLink>
              <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 px-10 py-6 text-lg font-semibold">
                Book Your AI Consultation
              </Button>
            </BookConsultationLink>
          </div>
        </div>
      </section>

      {/* Bottom spacing so sticky CTA clears footer */}
      <div className="h-20 sm:h-6" aria-hidden />
    </>
  );
}
